import * as React from 'react';
import { GoDecision, type GoDecision as GoDecisionCode } from '@clawhire/shared';
import { enumOptions } from '@/lib/format';
import { SimpleSelect } from '@/components/ui/select';
import { EnumBadge } from '@/components/data/EnumBadge';
import { InlineText } from '../components/InlineText';
import { StopRow } from '../components/StopRow';
import type { PositionListItem } from '../types';

export type PatchPosition = (id: string, patch: Record<string, unknown>) => Promise<unknown>;

/** Go/No-go queue: positions at stage New get an inline select, everything else a badge. */
export function GoDecisionCell({
  position,
  canEdit,
  onPatch,
}: {
  position: PositionListItem;
  canEdit: boolean;
  onPatch: PatchPosition;
}) {
  const [saving, setSaving] = React.useState(false);
  if (!canEdit || position.stage !== 'NEW') {
    return <EnumBadge def={GoDecision} value={position.goDecision} />;
  }
  return (
    <StopRow>
      <SimpleSelect
        aria-label={`Go decision for ${position.requestNo}`}
        value={position.goDecision}
        options={enumOptions(GoDecision)}
        disabled={saving}
        className="h-8 w-40"
        onChange={async (v) => {
          if (!v || v === position.goDecision) return;
          setSaving(true);
          try {
            await onPatch(position.id, { goDecision: v as GoDecisionCode });
          } finally {
            setSaving(false);
          }
        }}
      />
    </StopRow>
  );
}

export function GoReasonCell({
  position,
  canEdit,
  onPatch,
}: {
  position: PositionListItem;
  canEdit: boolean;
  onPatch: PatchPosition;
}) {
  if (!canEdit || position.stage !== 'NEW') {
    return (
      <span className={position.goReason ? undefined : 'text-muted-foreground'}>
        {position.goReason || '–'}
      </span>
    );
  }
  return (
    <StopRow>
      <InlineText
        value={position.goReason}
        label={`go reason for ${position.requestNo}`}
        placeholder="Add a reason"
        onCommit={(v) => onPatch(position.id, { goReason: v })}
      />
    </StopRow>
  );
}
